import { Locale } from "../Locale.ts";

/**
 * UI strings for the generator workbench (ADR 0061): the page header, the
 * rail's section titles, the actions above the sheet, the strip under it, the
 * mobile chip strip, bar and options sheet, and the copy column below.
 *
 * A separate area from `settings.ts` because none of these existed before the
 * redesign, and keeping them apart lets this file be read as the redesign's
 * whole vocabulary. Same guard as every other area: `es` and `fr` must satisfy
 * the English keys, so a missing translation fails `npm run typecheck:i18n`.
 */
const en = {
    "workbench.kicker": "Worksheet generator",
    "workbench.lede":
        "Set the numbers, print the page. Free, no account, a fresh set of problems " +
        "every time — with an answer key if you want one.",
    "workbench.sheetTag": "Sheet {code}",
    "workbench.onePage": "1 page",
    "workbench.pages": "{n} pages",
    "workbench.oneProblem": "1 problem",
    "workbench.problems": "{n} problems",
    "workbench.settingsAria": "Worksheet settings",
    "workbench.operation": "Operation",
    "workbench.problemFormat": "Problem format",
    "workbench.firstNumber": "First number",
    "workbench.secondNumber": "Second number",
    "workbench.digits": "Digits",
    "workbench.summaryAll": "all",
    "workbench.summaryNone": "none picked",
    "workbench.summaryMany": "{n} picked",
    "workbench.paper": "Paper",
    "workbench.problemsPerPage": "Problems per page",
    "workbench.pagesLabel": "Pages",
    "workbench.answerKey": "Answer key",
    "workbench.advanced": "Advanced",
    "workbench.hide": "hide",
    "workbench.printWorksheet": "Print worksheet",
    "workbench.downloadPdf": "Download PDF",
    "workbench.openPdfToPrint": "Open PDF to print",
    "workbench.buildingPdf": "Building PDF…",
    "workbench.worksheetActionsAria": "Worksheet actions",
    "workbench.sheetStatus": "code {code} prints on every sheet",
    "workbench.rebuildPrinted": "Rebuild a printed sheet",
    "workbench.copyHeading": "About {title}",
    "workbench.coversHeading": "What this worksheet covers",
    "workbench.standardsHeading": "Common Core standards",
    "workbench.sampleAlt": "Sample page from {title}",
    "workbench.sampleCaption": "A sample page. Every sheet is generated fresh, so yours will have different problems.",
    "workbench.browseAria": "Browse",
    "workbench.browseByGrade": "Worksheets by grade",
    "workbench.browseByTopic": "Worksheets by topic",
    "workbench.options": "Options",
    "workbench.showWorksheet": "Show the worksheet",
    "workbench.quickSettingsAria": "Quick settings",
    "workbench.perPageChip": "{n} / page",
    "workbench.oneDigitChip": "1 digit",
    "workbench.digitsChip": "{n} digits",
    "workbench.worksheetPreviewTitle": "Worksheet preview",
    "workbench.presentOpen": "Show on screen",
    "workbench.presentTitle": "Sheet {code} on screen",
    "workbench.presentProgress": "{current} of {total}",
    "workbench.presentShowAnswer": "Show answer",
    "workbench.presentHideAnswer": "Hide answer",
    "workbench.presentAnswerLabel": "Answer",
    "workbench.presentPrevious": "Previous",
    "workbench.presentNext": "Next",
    "workbench.presentClose": "Close",
    "workbench.presentFullScreen": "Full screen",
    "workbench.presentExitFullScreen": "Exit full screen",
    "workbench.presentHint": "Space shows the answer. Arrow keys move between problems. Escape closes.",
    "workbench.presentCopyLink": "Copy board link",
    "workbench.presentLinkCopied": "Link copied. It opens on this board.",
    "workbench.presentLinkAria": "Link that opens this sheet on the board",
} as const;

export type WorkbenchStringKey = keyof typeof en;

const es = {
    "workbench.kicker": "Generador de hojas",
    "workbench.lede":
        "Elige los números e imprime la hoja. Gratis, sin cuenta, con problemas nuevos " +
        "cada vez — y con hoja de respuestas si la quieres.",
    "workbench.sheetTag": "Hoja {code}",
    "workbench.onePage": "1 página",
    "workbench.pages": "{n} páginas",
    "workbench.oneProblem": "1 problema",
    "workbench.problems": "{n} problemas",
    "workbench.settingsAria": "Opciones de la hoja",
    "workbench.operation": "Operación",
    "workbench.problemFormat": "Tipo de problema",
    "workbench.firstNumber": "Primer número",
    "workbench.secondNumber": "Segundo número",
    "workbench.digits": "Dígitos",
    "workbench.summaryAll": "todos",
    "workbench.summaryNone": "ninguno",
    "workbench.summaryMany": "{n} elegidos",
    "workbench.paper": "Papel",
    "workbench.problemsPerPage": "Problemas por página",
    "workbench.pagesLabel": "Páginas",
    "workbench.answerKey": "Hoja de respuestas",
    "workbench.advanced": "Avanzado",
    "workbench.hide": "ocultar",
    "workbench.printWorksheet": "Imprimir la hoja",
    "workbench.downloadPdf": "Descargar PDF",
    "workbench.openPdfToPrint": "Abrir el PDF para imprimir",
    "workbench.buildingPdf": "Creando el PDF…",
    "workbench.worksheetActionsAria": "Acciones de la hoja",
    "workbench.sheetStatus": "el código {code} se imprime en cada hoja",
    "workbench.rebuildPrinted": "Rehacer una hoja impresa",
    "workbench.copyHeading": "Acerca de: {title}",
    "workbench.coversHeading": "Qué practica esta hoja",
    "workbench.standardsHeading": "Estándares Common Core",
    "workbench.sampleAlt": "Página de ejemplo de {title}",
    "workbench.sampleCaption": "Una página de ejemplo. Cada hoja se genera de nuevo, así que la tuya tendrá otros problemas.",
    "workbench.browseAria": "Browse",
    "workbench.browseByGrade": "Worksheets by grade",
    "workbench.browseByTopic": "Worksheets by topic",
    "workbench.options": "Opciones",
    "workbench.showWorksheet": "Ver la hoja",
    "workbench.quickSettingsAria": "Opciones rápidas",
    "workbench.perPageChip": "{n} / página",
    "workbench.oneDigitChip": "1 dígito",
    "workbench.digitsChip": "{n} dígitos",
    "workbench.worksheetPreviewTitle": "Vista previa de la hoja",
    "workbench.presentOpen": "Mostrar en pantalla",
    "workbench.presentTitle": "Hoja {code} en pantalla",
    "workbench.presentProgress": "{current} de {total}",
    "workbench.presentShowAnswer": "Mostrar respuesta",
    "workbench.presentHideAnswer": "Ocultar respuesta",
    "workbench.presentAnswerLabel": "Respuesta",
    "workbench.presentPrevious": "Anterior",
    "workbench.presentNext": "Siguiente",
    "workbench.presentClose": "Cerrar",
    "workbench.presentFullScreen": "Pantalla completa",
    "workbench.presentExitFullScreen": "Salir de pantalla completa",
    "workbench.presentHint": "La barra espaciadora muestra la respuesta. Las flechas cambian de problema. Escape cierra.",
    "workbench.presentCopyLink": "Copiar enlace de la pizarra",
    "workbench.presentLinkCopied": "Enlace copiado. Se abre en esta pizarra.",
    "workbench.presentLinkAria": "Enlace que abre esta hoja en la pizarra",
} satisfies Record<WorkbenchStringKey, string>;

const fr = {
    "workbench.kicker": "Générateur de fiches",
    "workbench.lede":
        "Choisissez les nombres, imprimez la page. Gratuit, sans compte, de nouveaux " +
        "problèmes à chaque fois — avec un corrigé si vous le souhaitez.",
    "workbench.sheetTag": "Fiche {code}",
    "workbench.onePage": "1 page",
    "workbench.pages": "{n} pages",
    "workbench.oneProblem": "1 problème",
    "workbench.problems": "{n} problèmes",
    "workbench.settingsAria": "Réglages de la fiche",
    "workbench.operation": "Opération",
    "workbench.problemFormat": "Type de problème",
    "workbench.firstNumber": "Premier nombre",
    "workbench.secondNumber": "Deuxième nombre",
    "workbench.digits": "Chiffres",
    "workbench.summaryAll": "tous",
    "workbench.summaryNone": "aucun",
    "workbench.summaryMany": "{n} choisis",
    "workbench.paper": "Papier",
    "workbench.problemsPerPage": "Problèmes par page",
    "workbench.pagesLabel": "Pages",
    "workbench.answerKey": "Corrigé",
    "workbench.advanced": "Avancé",
    "workbench.hide": "masquer",
    "workbench.printWorksheet": "Imprimer la fiche",
    "workbench.downloadPdf": "Télécharger le PDF",
    "workbench.openPdfToPrint": "Ouvrir le PDF pour imprimer",
    "workbench.buildingPdf": "Création du PDF…",
    "workbench.worksheetActionsAria": "Actions sur la fiche",
    "workbench.sheetStatus": "le code {code} est imprimé sur chaque fiche",
    "workbench.rebuildPrinted": "Refaire une fiche imprimée",
    "workbench.copyHeading": "À propos : {title}",
    "workbench.coversHeading": "Ce que travaille cette fiche",
    "workbench.standardsHeading": "Normes Common Core",
    "workbench.sampleAlt": "Page d’exemple : {title}",
    "workbench.sampleCaption": "Une page d’exemple. Chaque fiche est générée à nouveau, la vôtre aura donc d’autres problèmes.",
    "workbench.browseAria": "Browse",
    "workbench.browseByGrade": "Worksheets by grade",
    "workbench.browseByTopic": "Worksheets by topic",
    "workbench.options": "Réglages",
    "workbench.showWorksheet": "Voir la fiche",
    "workbench.quickSettingsAria": "Réglages rapides",
    "workbench.perPageChip": "{n} / page",
    "workbench.oneDigitChip": "1 chiffre",
    "workbench.digitsChip": "{n} chiffres",
    "workbench.worksheetPreviewTitle": "Aperçu de la fiche",
    "workbench.presentOpen": "Afficher à l'écran",
    "workbench.presentTitle": "Fiche {code} à l'écran",
    "workbench.presentProgress": "{current} sur {total}",
    "workbench.presentShowAnswer": "Afficher la réponse",
    "workbench.presentHideAnswer": "Masquer la réponse",
    "workbench.presentAnswerLabel": "Réponse",
    "workbench.presentPrevious": "Précédent",
    "workbench.presentNext": "Suivant",
    "workbench.presentClose": "Fermer",
    "workbench.presentFullScreen": "Plein écran",
    "workbench.presentExitFullScreen": "Quitter le plein écran",
    "workbench.presentHint": "La barre d'espace affiche la réponse. Les flèches changent de problème. Échap ferme.",
    "workbench.presentCopyLink": "Copier le lien du tableau",
    "workbench.presentLinkCopied": "Lien copié. Il s'ouvre sur ce tableau.",
    "workbench.presentLinkAria": "Lien qui ouvre cette fiche sur le tableau",
} satisfies Record<WorkbenchStringKey, string>;

export const workbenchStrings: Record<Locale, Record<WorkbenchStringKey, string>> = { en, es, fr };
